import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Phone, Mail, MessageCircle, Trash2, Clock, User, FileText, Save, Send, CheckCircle2, XCircle, AlertTriangle, Inbox } from "lucide-react";
import { toast } from "sonner";
import { getLead, updateStatus, addNote, deleteLead, leadEmails, sendEmailToLead } from "@/lib/admin-api";
import LeadInfoEditor from "@/components/admin/LeadInfoEditor";
import { formatMtyDateTime, formatMtyDateTimeShort, formatMtyRelative } from "@/lib/mty-date";

const STATUS_OPTIONS = [
  { v: "nuevo", l: "Nuevo", color: "bg-orange-500" },
  { v: "contactado", l: "Contactado", color: "bg-blue-500" },
  { v: "seguimiento", l: "Seguimiento", color: "bg-yellow-500" },
  { v: "cotizacion_enviada", l: "Cotización enviada", color: "bg-purple-500" },
  { v: "visita_agendada", l: "Visita agendada", color: "bg-indigo-500" },
  { v: "negociacion", l: "Negociación", color: "bg-pink-500" },
  { v: "anticipo_recibido", l: "Anticipo", color: "bg-emerald-500" },
  { v: "ganado", l: "Ganado", color: "bg-green-500" },
  { v: "perdido", l: "Perdido", color: "bg-neutral-500" },
];

const MANUAL_EMAIL_OPTIONS = [
  { v: "lead_confirmation", l: "Confirmación inicial", d: "El mismo email automático que recibió al registrarse." },
  { v: "lead_catalog_delivery", l: "Enviar catálogo PDF", d: "Email con el catálogo oficial adjunto + botón de descarga." },
  { v: "lead_followup_48h", l: "Follow-up 48h", d: "Recordatorio con CTAs de WhatsApp y llamada." },
  { v: "custom_to_lead", l: "Correo personalizado", d: "Escribe asunto y mensaje propios (con branding Eclipse)." },
];

function activityIcon(type) {
  if (type === "created") return <User className="w-4 h-4 text-orange-500" />;
  if (type === "status_changed") return <Clock className="w-4 h-4 text-blue-500" />;
  if (type === "note_added") return <FileText className="w-4 h-4 text-purple-500" />;
  if (type === "catalog_downloaded") return <FileText className="w-4 h-4 text-green-500" />;
  if (type === "email_sent_manually") return <Mail className="w-4 h-4 text-red-500" />;
  return <Clock className="w-4 h-4 text-neutral-400" />;
}

function statusBadge(s) {
  if (s === "sent") return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-800 border border-green-200"><CheckCircle2 className="w-3 h-3" /> Entregado</span>;
  if (s === "failed") return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-800 border border-red-200"><XCircle className="w-3 h-3" /> Fallo</span>;
  if (s === "skipped") return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-neutral-100 text-neutral-700 border border-neutral-200"><AlertTriangle className="w-3 h-3" /> Omitido</span>;
  return <span className="text-xs text-neutral-500">{s}</span>;
}

export default function AdminLeadDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [emails, setEmails] = useState([]);
  const [emailLabels, setEmailLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingStatus, setSavingStatus] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [sendType, setSendType] = useState("lead_confirmation");
  const [sendSubject, setSendSubject] = useState("");
  const [sendBody, setSendBody] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getLead(id);
      setData(d);
      const em = await leadEmails(id);
      setEmails(em.items || []);
      setEmailLabels(em.type_labels || {});
    } catch (e) {
      toast.error("Lead no encontrado");
      nav("/admin/leads");
    } finally {
      setLoading(false);
    }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-8 text-neutral-500">Cargando...</div>;
  if (!data) return null;
  const { lead, notes, activity } = data;

  const changeStatus = async (newEstatus) => {
    if (newEstatus === lead.estatus) return;
    setSavingStatus(true);
    try {
      await updateStatus(lead.id, newEstatus);
      toast.success(`Estatus: ${newEstatus}`);
      await load();
    } catch { toast.error("Error al actualizar"); }
    finally { setSavingStatus(false); }
  };

  const submitNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    setSavingNote(true);
    try {
      await addNote(lead.id, newNote.trim());
      setNewNote("");
      toast.success("Nota añadida");
      await load();
    } catch { toast.error("Error al guardar nota"); }
    finally { setSavingNote(false); }
  };

  const onDelete = async () => {
    if (!window.confirm(`¿Borrar lead de ${lead.nombre}? Esta acción no se puede deshacer.`)) return;
    try {
      await deleteLead(lead.id);
      toast.success("Lead eliminado");
      nav("/admin/leads");
    } catch { toast.error("Error al eliminar"); }
  };

  const sendManualEmail = async () => {
    if (!lead.email) return toast.error("Este lead no tiene email");
    if (sendType === "custom_to_lead" && (!sendSubject.trim() || !sendBody.trim())) {
      return toast.error("Asunto y mensaje son requeridos para correo personalizado");
    }
    setSending(true);
    try {
      const resp = await sendEmailToLead(lead.id, {
        email_type: sendType,
        custom_subject: sendType === "custom_to_lead" ? sendSubject.trim() : undefined,
        custom_body: sendType === "custom_to_lead" ? sendBody.trim() : undefined,
      });
      const status = resp.result?.status;
      if (status === "sent") toast.success("Correo enviado");
      else if (status === "skipped") toast.warning(`No enviado: ${resp.result?.reason || "Resend no configurado"}`);
      else toast.error(`Fallo: ${resp.result?.error || "desconocido"}`);
      setShowSend(false); setSendSubject(""); setSendBody("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al enviar");
    } finally { setSending(false); }
  };

  const cleanPhone = (lead.telefono || "").replace(/\D/g, "");
  const waUrl = `https://api.whatsapp.com/send?phone=${cleanPhone}&text=${encodeURIComponent(
    `Hola ${lead.nombre}, te contacto de Eclipse Cubiertas por tu solicitud de cotización FlexShade.`
  )}`;

  return (
    <div className="p-5 md:p-8 max-w-6xl">
      <Link to="/admin/leads" className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> Volver a leads
      </Link>

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="section-title text-3xl md:text-4xl font-black">{lead.nombre}</h1>
            <ScoreBadge score={lead.lead_score} temp={lead.lead_temp} />
          </div>
          <p className="text-neutral-500 text-sm mt-1 capitalize">
            {lead.tipo_espacio || "-"} · {lead.ciudad || "-"} · Recibido {formatMtyDateTime(lead.created_at)}
          </p>
          {lead.loss_reason && (
            <p className="text-xs mt-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-neutral-100 text-neutral-700 border border-neutral-200">
              <AlertTriangle className="w-3 h-3" /> Motivo de pérdida: <strong>{lead.loss_reason}</strong>
              {lead.loss_reason_note ? ` · ${lead.loss_reason_note}` : ""}
            </p>
          )}
        </div>
        <button onClick={onDelete} className="inline-flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg" data-testid="delete-lead">
          <Trash2 className="w-4 h-4" /> Eliminar
        </button>
      </div>

      {/* Quick actions */}
      <div className="flex flex-wrap gap-2 mb-6">
        <a href={waUrl} target="_blank" rel="noreferrer" className="btn-wa px-4 py-2.5 rounded-lg inline-flex items-center gap-2 text-sm">
          <MessageCircle className="w-4 h-4" /> WhatsApp
        </a>
        <a href={`tel:${lead.telefono}`} className="bg-black text-white px-4 py-2.5 rounded-lg inline-flex items-center gap-2 text-sm font-semibold hover:bg-neutral-800">
          <Phone className="w-4 h-4" /> Llamar
        </a>
        {lead.email && (
          <a href={`mailto:${lead.email}`} className="bg-neutral-100 text-neutral-900 px-4 py-2.5 rounded-lg inline-flex items-center gap-2 text-sm font-semibold hover:bg-neutral-200">
            <Mail className="w-4 h-4" /> Email cliente
          </a>
        )}
        {lead.email && (
          <button onClick={() => setShowSend(true)} className="btn-primary px-4 py-2.5 rounded-lg inline-flex items-center gap-2 text-sm" data-testid="open-send-email">
            <Send className="w-4 h-4" /> Enviar correo
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left: Lead data */}
        <div className="lg:col-span-2 space-y-5">
          {/* Status selector */}
          <div className="bg-white border border-neutral-200 rounded-2xl p-5">
            <h3 className="font-bold text-base mb-3">Estatus del lead</h3>
            <div className="flex flex-wrap gap-2" data-testid="status-selector">
              {STATUS_OPTIONS.map((s) => (
                <button key={s.v} onClick={() => changeStatus(s.v)} disabled={savingStatus} data-testid={`status-btn-${s.v}`}
                  className={`px-3.5 py-2 rounded-lg text-xs font-semibold border-2 transition ${
                    lead.estatus === s.v ? `${s.color} text-white border-transparent` : "bg-white text-neutral-700 border-neutral-200 hover:border-neutral-400"
                  }`}>
                  {s.l}
                </button>
              ))}
            </div>
          </div>

          {/* Info */}
          <LeadInfoEditor lead={lead} onSaved={load} />

          {/* UTM tracking */}
          {(lead.utm_source || lead.utm_campaign || lead.referrer) && (
            <div className="bg-white border border-neutral-200 rounded-2xl p-5">
              <h4 className="text-xs font-bold text-neutral-500 uppercase tracking-wider mb-2">Tracking Google Ads</h4>
              <div className="flex flex-wrap gap-2 text-xs">
                {lead.utm_source && <Pill label="source" value={lead.utm_source} />}
                {lead.utm_medium && <Pill label="medium" value={lead.utm_medium} />}
                {lead.utm_campaign && <Pill label="campaign" value={lead.utm_campaign} />}
                {lead.utm_term && <Pill label="term" value={lead.utm_term} />}
                {lead.utm_content && <Pill label="content" value={lead.utm_content} />}
                {lead.referrer && <Pill label="referrer" value={lead.referrer} />}
              </div>
            </div>
          )}

          {/* Emails sent */}
          <div className="bg-white border border-neutral-200 rounded-2xl p-5" data-testid="emails-section">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-base flex items-center gap-2">
                <Inbox className="w-4 h-4" /> Correos enviados
                <span className="text-xs font-semibold bg-neutral-100 text-neutral-700 px-2 py-0.5 rounded-full">{emails.length}</span>
              </h3>
              {lead.email && (
                <button onClick={() => setShowSend(true)} className="text-xs font-semibold text-red-600 hover:text-red-700 inline-flex items-center gap-1">
                  <Send className="w-3 h-3" /> Enviar nuevo
                </button>
              )}
            </div>
            {emails.length === 0 ? (
              <p className="text-sm text-neutral-400 text-center py-4">Aún no se han enviado correos.</p>
            ) : (
              <ul className="space-y-2">
                {emails.map((e) => (
                  <li key={e.id} className="flex items-start justify-between gap-3 p-3 bg-neutral-50 rounded-lg border border-neutral-100">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-neutral-900">{emailLabels[e.email_type] || e.email_type}</span>
                        {statusBadge(e.status)}
                      </div>
                      <p className="text-xs text-neutral-600 mt-1 truncate">{e.subject}</p>
                      <p className="text-[11px] text-neutral-400 mt-0.5">
                        {e.triggered_by === "system" ? "Automático" : `Manual por ${e.triggered_by}`} · {formatMtyDateTimeShort(e.created_at)} · a {e.to}
                      </p>
                      {e.error && <p className="text-[11px] text-red-500 mt-0.5 truncate" title={e.error}>⚠ {e.error}</p>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Notes */}
          <div className="bg-white border border-neutral-200 rounded-2xl p-5">
            <h3 className="font-bold text-base mb-3">Notas internas</h3>
            <form onSubmit={submitNote} className="mb-4" data-testid="note-form">
              <textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Escribe una nota interna..." rows={3} className="form-input" data-testid="note-input" />
              <div className="flex justify-end mt-2">
                <button type="submit" disabled={savingNote || !newNote.trim()} className="btn-primary px-4 py-2 rounded-lg inline-flex items-center gap-2 text-sm disabled:opacity-60" data-testid="note-submit">
                  <Save className="w-4 h-4" /> {savingNote ? "Guardando..." : "Guardar nota"}
                </button>
              </div>
            </form>
            <div className="space-y-3">
              {notes.length === 0 ? (
                <p className="text-sm text-neutral-400 text-center py-4">Sin notas todavía.</p>
              ) : notes.map((n) => (
                <div key={n.id} className="bg-neutral-50 rounded-lg p-3 border border-neutral-100">
                  <p className="text-sm text-neutral-800 whitespace-pre-wrap">{n.nota}</p>
                  <p className="text-xs text-neutral-500 mt-1.5">{n.by || "Sistema"} · {formatMtyDateTime(n.created_at)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Timeline */}
        <div className="bg-white border border-neutral-200 rounded-2xl p-5 h-fit">
          <h3 className="font-bold text-base mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4" /> Timeline de actividad
          </h3>
          <div className="space-y-4">
            {activity.length === 0 ? (
              <p className="text-sm text-neutral-400">Sin actividad.</p>
            ) : activity.map((a) => (
              <div key={a.id} className="flex gap-3 text-sm">
                <div className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center flex-shrink-0">{activityIcon(a.type)}</div>
                <div className="flex-1 min-w-0 pt-1">
                  <p className="text-neutral-800 leading-snug">{a.description}</p>
                  <p className="text-xs text-neutral-500 mt-0.5">{a.by || "sistema"} · {formatMtyDateTimeShort(a.created_at)} · {formatMtyRelative(a.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Send email modal */}
      {showSend && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => !sending && setShowSend(false)}>
          <div className="bg-white rounded-2xl max-w-xl w-full p-6 md:p-7 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-black mb-1 section-title">Enviar correo al lead</h2>
            <p className="text-sm text-neutral-500 mb-5">Destinatario: <strong>{lead.email}</strong></p>

            <label className="form-label">Tipo de correo</label>
            <div className="space-y-2 mb-4">
              {MANUAL_EMAIL_OPTIONS.map((opt) => (
                <label key={opt.v} className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition ${sendType === opt.v ? "border-red-500 bg-red-50" : "border-neutral-200 hover:border-neutral-300"}`}>
                  <input type="radio" name="email_type" value={opt.v} checked={sendType === opt.v} onChange={(e) => setSendType(e.target.value)} className="mt-1" data-testid={`send-type-${opt.v}`} />
                  <div>
                    <p className="font-semibold text-sm">{opt.l}</p>
                    <p className="text-xs text-neutral-500 mt-0.5">{opt.d}</p>
                  </div>
                </label>
              ))}
            </div>

            {sendType === "custom_to_lead" && (
              <div className="space-y-3 mb-4">
                <div>
                  <label className="form-label">Asunto *</label>
                  <input value={sendSubject} onChange={(e) => setSendSubject(e.target.value)} placeholder="Ej. Tu cotización FlexShade" className="form-input" data-testid="custom-subject" />
                </div>
                <div>
                  <label className="form-label">Mensaje *</label>
                  <textarea rows={6} value={sendBody} onChange={(e) => setSendBody(e.target.value)} placeholder="Hola, gracias por tu interés..." className="form-input" data-testid="custom-body" />
                  <p className="text-[11px] text-neutral-400 mt-1">Se envía con branding Eclipse (logo, colores, firma, botones WhatsApp/Llamar).</p>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-neutral-100">
              <button onClick={() => setShowSend(false)} disabled={sending} className="px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-100 rounded-lg">Cancelar</button>
              <button onClick={sendManualEmail} disabled={sending} className="btn-primary px-5 py-2 rounded-lg inline-flex items-center gap-2 text-sm disabled:opacity-60" data-testid="confirm-send-email">
                <Send className="w-4 h-4" /> {sending ? "Enviando..." : "Enviar correo"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoItem({ label, value }) {
  return (
    <div>
      <dt className="text-xs text-neutral-500 font-medium">{label}</dt>
      <dd className="text-neutral-900 text-sm mt-0.5 capitalize break-words">{value}</dd>
    </div>
  );
}

function Pill({ label, value }) {
  return (
    <span className="inline-flex items-center gap-1.5 bg-neutral-100 border border-neutral-200 rounded-full px-2.5 py-1 font-mono">
      <span className="text-neutral-500">{label}:</span>
      <span className="text-neutral-900 font-semibold truncate max-w-[200px]">{value}</span>
    </span>
  );
}

function ScoreBadge({ score, temp }) {
  const t = temp || "cold";
  const styles = t === "hot"
    ? "bg-red-600 text-white"
    : t === "warm"
    ? "bg-amber-500 text-white"
    : "bg-neutral-300 text-neutral-800";
  const label = t === "hot" ? "HOT" : t === "warm" ? "WARM" : "COLD";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${styles}`}
      data-testid="lead-score-badge"
      title="Score automático del lead (0-100)"
    >
      {label} · {score ?? 0}/100
    </span>
  );
}
